import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/Layout";
import { toast } from "sonner";
import {
  Instagram,
  Facebook,
  CheckCircle2,
  Plug,
  Zap,
  TrendingUp,
  AlertCircle,
  Send,
  Activity,
  PlayCircle,
  Plus,
  Trash2,
  Loader2,
  ShieldAlert,
  MailCheck,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";

function Stat({ label, value, sub, accent, icon: Icon, testid }) {
  return (
    <div className={`brutal p-5 ${accent || "bg-white"}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-xs font-bold uppercase tracking-wider">{label}</div>
        {Icon && <Icon className="w-4 h-4" strokeWidth={2.5} />}
      </div>
      <div className="font-heading font-black text-4xl mt-2">{value}</div>
      {sub && <div className="text-xs text-muted-foreground font-semibold mt-1">{sub}</div>}
    </div>
  );
}

const PLATFORM_ICON = { instagram: Instagram, facebook: Facebook };
const PLATFORM_LABEL = { instagram: "Instagram", facebook: "Facebook Page" };
const LOGIN_METHOD_LABEL = {
  facebook: "Facebook",
  google: "Google",
  microsoft: "Microsoft",
  manual: "Email and password",
};
const CONNECT_ERROR_MESSAGE = {
  oauth_disabled: "Meta connection is not enabled yet.",
  invalid_state: "Connection session expired. Please try again.",
  authorization_failed: "Meta could not complete authorization. Check that Web OAuth Login is on and this account is added as a tester.",
  exchange_failed: "Meta could not complete authorization. Check that Web OAuth Login is on and this account is added as a tester.",
  page_permission_failed: "Meta could not read your Pages. Check that this tester can manage the Page and accepted the Page permissions.",
  no_facebook_page: "No Facebook Page was found on that Meta account.",
  no_instagram_business: "No Instagram Business account is linked to that Meta account.",
};

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [stats, setStats] = useState(null);
  const [triggers, setTriggers] = useState([]);
  const [postCommentCounts, setPostCommentCounts] = useState({});
  const [connectConfig, setConnectConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const [connectPlatform, setConnectPlatform] = useState("instagram");
  const [igUsername, setIgUsername] = useState("");
  const [pageName, setPageName] = useState("");
  const [showSimulate, setShowSimulate] = useState(false);
  const [simComment, setSimComment] = useState("LINK please!");
  const [simUser, setSimUser] = useState("test_user");
  const [simPost, setSimPost] = useState("");
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deleteOtpSent, setDeleteOtpSent] = useState(false);
  const [deleteOtp, setDeleteOtp] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [igRes, sRes, tRes, countsRes, cfgRes] = await Promise.all([
        api.get("/instagram/status"),
        api.get("/analytics/summary"),
        api.get("/triggers?limit=8"),
        api.get("/analytics/post-comment-counts").catch(() => ({ data: {} })),
        api.get("/instagram/oauth/config").catch(() => ({ data: null })),
      ]);
      setAccounts(igRes.data.accounts || []);
      setStats(sRes.data);
      setTriggers(tRes.data);
      setPostCommentCounts(countsRes.data || {});
      setConnectConfig(cfgRes.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const connected = params.get("connected") || (params.get("ig_connected") ? "instagram" : "");
    const connectError = params.get("connect_error");
    if (!connected && !connectError) return;

    if (connected) {
      toast.success(`${PLATFORM_LABEL[connected] || "Channel"} connected!`);
      load();
    }
    if (connectError) {
      toast.error(CONNECT_ERROR_MESSAGE[connectError] || "Could not connect that channel.");
    }

    const next = new URLSearchParams(params);
    next.delete("connected");
    next.delete("ig_connected");
    next.delete("connect_error");
    setParams(next, { replace: true });
  }, [load, params, setParams]);

const handleConnect = async (e) => {
  e.preventDefault();
  setConnecting(true);
  try {
    if (connectConfig?.oauth_enabled) {
      const { data } = await api.get("/instagram/oauth/start", {
        params: {
          platform: connectPlatform,
          handle: igUsername,
          display_name: pageName || undefined,
        },
      });
      window.location.href = data.url;
      return;
    }

    await api.post("/instagram/connect", {
      ig_username: igUsername,
      page_name: pageName,
      platform: connectPlatform,
    });
    toast.success(`${PLATFORM_LABEL[connectPlatform]} connected!`);
    setShowConnect(false);
    setIgUsername("");
    setPageName("");
    load();
  } catch (e) {
    toast.error(formatApiError(e.response?.data?.detail));
  } finally {
    setConnecting(false);
  }
};

  const handleDirectProfile = () => {
    const cleanUsername = igUsername.trim().replace(/^@/, "");
    if (cleanUsername) {
      if (!/^[a-zA-Z0-9._-]{1,80}$/.test(cleanUsername)) {
        toast.error("Use only letters, numbers, dots, underscores, or hyphens.");
        return;
      }
      if (connectPlatform === "instagram") {
        window.open(`https://instagram.com/${cleanUsername}`, "_blank", "noopener,noreferrer");
        toast.success(`Opened instagram.com/${cleanUsername}`);
      } else if (connectPlatform === "facebook") {
        window.open(`https://facebook.com/${cleanUsername}`, "_blank", "noopener,noreferrer");
        toast.success(`Opened facebook.com/${cleanUsername}`);
      }
      setShowConnect(false);
    } else {
      toast.error(`Enter ${connectPlatform === "facebook" ? "Facebook Page" : "Instagram"} username first`);
    }
  };

  const handleDisconnect = async (platform) => {
    if (!window.confirm(`Disconnect ${PLATFORM_LABEL[platform]}?`)) return;
    await api.post(`/instagram/disconnect?platform=${platform}`);
    toast.success("Disconnected");
    load();
  };

  const handleSimulate = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/webhook/simulate", {
        comment_text: simComment,
        commenter_username: simUser,
        post_id: simPost || null,
      });
      const status = res.data.status;
      if (status === "sent") toast.success(`DM sent to @${simUser}!`);
      else if (status === "no_match") toast.warning("No rule matched this comment");
      else if (status === "quota_exceeded") toast.error("Free plan limit reached");
      else if (status === "duplicate") toast.info("Duplicate (already processed)");
      else if (status === "failed") toast.error("DM failed (simulated)");
      else toast.info(status);
      setShowSimulate(false);
      load();
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const requestDeleteOtp = async () => {
    setDeleteBusy(true);
    try {
      await api.post("/account/delete/request");
      setDeleteOtpSent(true);
      toast.success("Deletion OTP sent to your email.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail ?? e.response?.data ?? e.message));
    } finally {
      setDeleteBusy(false);
    }
  };

  const confirmAccountDelete = async (e) => {
    e.preventDefault();
    setDeleteBusy(true);
    try {
      await api.post("/account/delete/confirm", { otp: deleteOtp.trim() });
      toast.success("Account deleted.");
      localStorage.removeItem("access_token");
      window.location.assign("/login");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail ?? e.response?.data ?? e.message));
    } finally {
      setDeleteBusy(false);
    }
  };

  if (loading) return <div className="brutal p-6 inline-block">Loading…</div>;

  const hasAccounts = accounts.length > 0;
  const useMetaOAuth = Boolean(connectConfig?.oauth_enabled);
  const manualConnectEnabled = connectConfig ? connectConfig.mock_mode : true;
  const connectDisabled = connecting || (!useMetaOAuth && !manualConnectEnabled);
  const loginMethods = user?.login_methods?.length ? user.login_methods : [user?.primary_login_method || "manual"];
  const loginMethodText = loginMethods.map((m) => LOGIN_METHOD_LABEL[m] || m).join(", ");
  const topPostCommentCount = Object.values(postCommentCounts).length ? Math.max(...Object.values(postCommentCounts)) : 0;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="chip bg-mint">Dashboard</span>
          <h1 className="text-4xl md:text-5xl font-extrabold mt-3">Hey, {user?.name?.split(" ")[0] || "there"} 👋</h1>
          <p className="text-muted-foreground font-medium mt-1">Here's what's happening with your DMs.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/app/rules" data-testid="quick-create-rule" className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-primary border-2 border-foreground rounded-md font-bold shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 transition-all text-sm">
            <Plus className="w-4 h-4" strokeWidth={2.5} /> New rule
          </Link>
          <button onClick={() => setShowSimulate(true)} data-testid="simulate-btn" disabled={!hasAccounts} className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-accent border-2 border-foreground rounded-md font-bold shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 transition-all text-sm disabled:opacity-50">
            <PlayCircle className="w-4 h-4" strokeWidth={2.5} /> Simulate comment
          </button>
        </div>
      </header>

      {/* Connected accounts */}
      <div className="brutal p-6 bg-secondary">
        <div className="flex items-end justify-between gap-4 mb-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider">Connected Accounts</div>
            <h2 className="font-heading font-extrabold text-xl mt-1">
              {hasAccounts ? `${accounts.length} channel${accounts.length > 1 ? "s" : ""} active` : "No accounts connected yet"}
            </h2>
          </div>
          <button onClick={() => { setConnectPlatform("instagram"); setShowConnect(true); }} data-testid="connect-account" className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-primary border-2 border-foreground rounded-md font-bold text-sm shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 transition-all">
            <Plug className="w-4 h-4" strokeWidth={2.5} /> Add account
          </button>
        </div>
        {!hasAccounts ? (
          <p className="text-sm font-medium text-muted-foreground">Connect Instagram or a Facebook Page to start receiving comment events. <Link to="/app/platforms" className="font-bold underline">See all platforms →</Link></p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {accounts.map((a) => {
              const Icon = PLATFORM_ICON[a.platform] || Plug;
              return (
                <div key={a.id} className="bg-white border-2 border-foreground rounded-md p-3 flex items-center gap-3" data-testid={`acct-${a.platform}`}>
                  <div className="w-10 h-10 bg-secondary border-2 border-foreground rounded-md flex items-center justify-center"><Icon className="w-5 h-5" strokeWidth={2.5} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">@{a.ig_username}</div>
                    <div className="text-xs text-muted-foreground font-semibold flex items-center gap-1"><CheckCircle2 className="w-3 h-3" strokeWidth={3} /> {PLATFORM_LABEL[a.platform]}</div>
                  </div>
                  <button onClick={() => handleDisconnect(a.platform)} title="Disconnect" data-testid={`disconnect-${a.platform}`} className="p-2 border-2 border-foreground rounded-md bg-white hover:bg-destructive hover:text-white transition-colors">
                    <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <Stat testid="stat-triggers" label="Total triggers" value={stats?.total_triggers ?? 0} icon={Zap} accent="bg-white" />
        <Stat testid="stat-sent" label="DMs sent" value={stats?.dms_sent ?? 0} icon={Send} accent="bg-mint" />
        <Stat testid="stat-failed" label="Failed" value={stats?.dms_failed ?? 0} icon={AlertCircle} accent="bg-peach" />
        <Stat testid="stat-rate" label="Success rate" value={`${stats?.success_rate ?? 0}%`} icon={TrendingUp} accent="bg-butter" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-4">
        <Stat testid="stat-top-comments" label="Top post comments" value={topPostCommentCount} icon={Activity} accent="bg-secondary" />
      </div>

      {/* Plan usage */}
      <div className="brutal p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider">Monthly Usage</div>
            <div className="font-heading font-extrabold text-2xl mt-1" data-testid="usage-text">
              {stats?.monthly_used ?? 0} / {stats?.monthly_limit ?? "∞"}
            </div>
          </div>
          <div className="chip bg-accent">{stats?.plan?.toUpperCase()}</div>
        </div>
        {stats?.monthly_limit && (
          <div className="h-4 bg-muted border-2 border-foreground rounded-full overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${Math.min(100, (stats.monthly_used / stats.monthly_limit) * 100)}%` }} />
          </div>
        )}
        {stats?.plan === "free" && stats?.monthly_used >= (stats?.monthly_limit ?? 0) * 0.8 && (
          <div className="mt-3 text-sm font-semibold flex items-center justify-between">
            <span>You're close to your limit. Upgrade to Pro for unlimited.</span>
            <Link to="/app/billing" className="px-3 py-1.5 bg-primary border-2 border-foreground rounded-md text-sm font-bold shadow-brutal-sm">Upgrade</Link>
          </div>
        )}
      </div>

      {/* Account */}
      <div className="brutal p-6 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider">Account</div>
            <div className="font-heading font-extrabold text-xl mt-1">Profile and deletion</div>
            <div className="text-sm text-muted-foreground font-medium mt-1">
              User ID: <span className="font-mono font-bold text-foreground">{user?.user_uid || user?.id}</span>
            </div>
            <div className="text-sm text-muted-foreground font-medium mt-1 flex flex-wrap items-center gap-2">
              <MailCheck className="w-4 h-4" strokeWidth={2.5} />
              <span>Logged in with <span className="font-bold text-foreground">{loginMethodText}</span></span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setShowDeleteAccount(true);
              setDeleteOtpSent(false);
              setDeleteOtp("");
            }}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-white border-2 border-foreground rounded-md font-bold shadow-brutal-sm hover:bg-destructive hover:text-white text-sm"
          >
            <ShieldAlert className="w-4 h-4" strokeWidth={2.5} /> Delete account
          </button>
        </div>
      </div>

      {/* Recent triggers */}
      <div className="brutal p-0 bg-white overflow-hidden">
        <div className="p-5 border-b-2 border-foreground flex items-center justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider">Recent Activity</div>
            <h3 className="font-heading font-extrabold text-xl">Latest triggers</h3>
          </div>
          <Link to="/app/triggers" className="text-sm font-bold underline">View all →</Link>
        </div>
        {triggers.length === 0 ? (
          <div className="p-10 text-center">
            <Activity className="w-10 h-10 mx-auto mb-3 text-muted-foreground" strokeWidth={2} />
            <div className="font-bold">No triggers yet.</div>
            <div className="text-sm text-muted-foreground font-medium">Create a rule and simulate a comment to see DmFlio in action.</div>
          </div>
        ) : (
          <div className="divide-y-2 divide-foreground">
            {triggers.map((t) => (
              <div key={t.id} className="p-4 flex flex-wrap items-center gap-3 justify-between" data-testid={`trigger-row-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold">@{t.commenter_username}</span>
                    {t.platform && <span className="chip bg-secondary text-[10px]">{t.platform}</span>}
                    <span className="text-xs text-muted-foreground font-semibold">matched</span>
                    <span className="chip bg-butter">{t.keyword_matched}</span>
                  </div>
                  <div className="text-sm text-muted-foreground font-medium mt-1 truncate">"{t.comment_text}"</div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center gap-3">
                    <StatusBadge status={t.dm_status} />
                    <span className="text-xs text-muted-foreground font-semibold">{new Date(t.created_at).toLocaleString()}</span>
                  </div>
                  {t.post_id && (
                    <span className="chip bg-muted text-muted-foreground text-[10px] uppercase tracking-wider">
                      Comments: {postCommentCounts[t.post_id] ?? 1}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Connect Modal */}
      {showConnect && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setShowConnect(false)}>
          <form onSubmit={handleConnect} onClick={(e) => e.stopPropagation()} className="brutal bg-white p-6 max-w-md w-full space-y-4">
            <div>
              <h3 className="font-heading font-extrabold text-2xl">Connect Account</h3>
              <p className="text-sm text-muted-foreground font-medium mt-1">Choose platform and enter account details.</p>
            </div>
            
            {/* Platform Selection */}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block">Platform</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setConnectPlatform("instagram")}
                  className={`p-3 border-2 rounded-md font-bold text-sm flex items-center gap-2 transition-colors ${
                    connectPlatform === "instagram" 
                      ? "bg-primary border-foreground" 
                      : "bg-muted border-foreground hover:bg-secondary"
                  }`}
                >
                  <Instagram className="w-4 h-4" strokeWidth={2.5} />
                  Instagram
                </button>
                <button
                  type="button"
                  onClick={() => setConnectPlatform("facebook")}
                  className={`p-3 border-2 rounded-md font-bold text-sm flex items-center gap-2 transition-colors ${
                    connectPlatform === "facebook" 
                      ? "bg-primary border-foreground" 
                      : "bg-muted border-foreground hover:bg-secondary"
                  }`}
                >
                  <Facebook className="w-4 h-4" strokeWidth={2.5} />
                  Facebook Page
                </button>
              </div>
            </div>
            
            <div>
              <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block">
                {connectPlatform === "facebook" ? "Facebook Page name/ID" : "Instagram username/ID"}
              </label>
              <input 
                required 
                value={igUsername} 
                onChange={(e) => setIgUsername(e.target.value)} 
                data-testid="ig-username" 
                className="brutal-input w-full" 
                placeholder={connectPlatform === "facebook" ? "Your Page name" : "@yourbrand"} 
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block">Display name (optional)</label>
              <input 
                value={pageName} 
                onChange={(e) => setPageName(e.target.value)} 
                data-testid="ig-page-name" 
                className="brutal-input w-full" 
                placeholder="Your Brand Page" 
              />
            </div>
            
            {/* Instructions */}
            <div className="text-xs text-muted-foreground bg-muted border-2 border-foreground rounded-md p-2.5">
              {connectPlatform === "instagram"
                ? "Choose: OAuth (Meta connect for DM automation) or Direct Profile (open Instagram)"
                : "Enter Facebook Page name/ID then connect via Meta."}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              {connectPlatform === "instagram" && !useMetaOAuth && (
                <button 
                  type="button" 
                  onClick={handleDirectProfile} 
                  className="flex-1 py-2.5 bg-accent border-2 border-foreground rounded-md font-bold shadow-brutal-sm hover:shadow-brutal"
                >
                  Direct Profile
                </button>
              )}
              <button 
                type="submit" 
                disabled={connectDisabled || !igUsername.trim()} 
                data-testid="ig-connect-submit" 
                className={`flex-1 py-2.5 bg-primary border-2 border-foreground rounded-md font-bold shadow-brutal-sm disabled:opacity-50 inline-flex items-center justify-center gap-2 hover:shadow-brutal ${connectPlatform === "instagram" && !useMetaOAuth ? "" : "w-full"}`}
              >
                {connecting && <Loader2 className="w-4 h-4 animate-spin" />}
                {useMetaOAuth ? "Connect via Meta" : "Connect"}
              </button>
            </div>
            <button 
              type="button" 
              onClick={() => setShowConnect(false)} 
              className="w-full py-2.5 border-2 border-foreground rounded-md font-bold bg-muted"
            >
              Cancel
            </button>
          </form>
        </div>
      )}

      {/* Simulate Modal */}
      {showSimulate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setShowSimulate(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={handleSimulate} className="brutal bg-white p-6 max-w-md w-full space-y-4">
            <div>
              <h3 className="font-heading font-extrabold text-2xl">Simulate a comment</h3>
              <p className="text-sm text-muted-foreground font-medium mt-1">Test your rules without waiting for real comments.</p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block">Commenter username</label>
              <input required value={simUser} onChange={(e) => setSimUser(e.target.value)} data-testid="sim-username" className="brutal-input w-full" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block">Comment text</label>
              <input required value={simComment} onChange={(e) => setSimComment(e.target.value)} data-testid="sim-comment" className="brutal-input w-full" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block">Post link or ID</label>
              <input value={simPost} onChange={(e) => setSimPost(e.target.value)} data-testid="sim-post-id" className="brutal-input w-full" placeholder="Leave empty for all-post rules" />
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setShowSimulate(false)} className="flex-1 py-2.5 border-2 border-foreground rounded-md font-bold">Cancel</button>
              <button type="submit" data-testid="sim-submit" className="flex-1 py-2.5 bg-accent border-2 border-foreground rounded-md font-bold shadow-brutal-sm">Run simulation</button>
            </div>
          </form>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteAccount && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => !deleteBusy && setShowDeleteAccount(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={confirmAccountDelete} className="brutal bg-white p-6 max-w-md w-full space-y-4">
            <div>
              <h3 className="font-heading font-extrabold text-2xl">Delete account</h3>
              <p className="text-sm text-muted-foreground font-medium mt-1">This disables your login and keeps recovery available through admin support.</p>
            </div>
            {!deleteOtpSent ? (
              <button
                type="button"
                onClick={requestDeleteOtp}
                disabled={deleteBusy}
                className="w-full py-2.5 bg-primary border-2 border-foreground rounded-md font-bold shadow-brutal-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {deleteBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailCheck className="w-4 h-4" strokeWidth={2.5} />}
                Send OTP
              </button>
            ) : (
              <>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block">Email OTP</label>
                  <input
                    required
                    value={deleteOtp}
                    onChange={(e) => setDeleteOtp(e.target.value)}
                    className="brutal-input w-full"
                    inputMode="numeric"
                    maxLength={12}
                    placeholder="Enter OTP"
                  />
                </div>
                <button
                  type="submit"
                  disabled={deleteBusy || deleteOtp.trim().length < 4}
                  className="w-full py-2.5 bg-destructive text-white border-2 border-foreground rounded-md font-bold shadow-brutal-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                  {deleteBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                  Confirm delete
                </button>
              </>
            )}
            <button
              type="button"
              disabled={deleteBusy}
              onClick={() => setShowDeleteAccount(false)}
              className="w-full py-2.5 border-2 border-foreground rounded-md font-bold bg-muted disabled:opacity-50"
            >
              Cancel
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
